import { useEffect, useRef, useState } from "react"
import type { AppShell } from "@/App"
import { errorMessage, getGoogleMapsConfig, getRouteGeometry, type Order, type PlanStop, type Warehouse } from "@/lib/api"
import { n } from "@/lib/format"
import { loadGoogleMaps, onGoogleMapsProblem } from "@/lib/google-maps"

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type G = any

/** Polyline do OSRM (precisão 5) → [{lat, lng}]. */
export function decodePolyline(str: string): Array<{ lat: number; lng: number }> {
  const out: Array<{ lat: number; lng: number }> = []
  let i = 0
  let lat = 0
  let lng = 0
  while (i < str.length) {
    for (const axis of [0, 1]) {
      let shift = 0
      let result = 0
      let b: number
      do {
        b = str.charCodeAt(i++) - 63
        result |= (b & 0x1f) << shift
        shift += 5
      } while (b >= 0x20)
      const d = result & 1 ? ~(result >> 1) : result >> 1
      if (axis === 0) lat += d
      else lng += d
    }
    out.push({ lat: lat / 1e5, lng: lng / 1e5 })
  }
  return out
}

let keyPromise: Promise<{ enabled: boolean; apiKey: string | null }> | null = null

/**
 * Mapa da rota aberta (D7): Google Maps do tenant com o percurso do OSRM, o
 * armazém e as paragens numeradas. Ligado à lista: clicar numa paragem
 * seleciona a linha e vice-versa.
 */
export function RouteMap({
  shell,
  routeId,
  stops,
  orders,
  warehouse,
  selected,
  onSelect,
}: {
  shell: AppShell
  routeId: string
  stops: PlanStop[]
  orders: Map<string, Order>
  warehouse: Warehouse | null
  selected: string | null
  onSelect: (orderId: string) => void
}) {
  const box = useRef<HTMLDivElement>(null)
  const map = useRef<G>(null)
  const layers = useRef<G[]>([])
  const markers = useRef<Map<string, G>>(new Map())
  const [state, setState] = useState<"loading" | "ready" | "off" | string>("loading")
  const [problem, setProblem] = useState<string | null>(null)
  useEffect(() => onGoogleMapsProblem(setProblem), [])

  // Carrega a API (uma vez) e cria o mapa.
  useEffect(() => {
    let cancelled = false
    keyPromise ??= getGoogleMapsConfig(shell)
    keyPromise
      .then(async (cfg) => {
        if (!cfg.enabled || !cfg.apiKey) return setState("off")
        const google = await loadGoogleMaps(cfg.apiKey)
        const { Map } = await google.maps.importLibrary("maps")
        if (cancelled || !box.current) return
        map.current ??= new Map(box.current, { center: { lat: 39.8, lng: -8.7 }, zoom: 8, zoomControl: true, mapTypeControl: false, streetViewControl: false, fullscreenControl: true })
        setState("ready")
      })
      .catch((err) => {
        keyPromise = null
        setState(errorMessage(err))
      })
    return () => {
      cancelled = true
    }
  }, [shell])

  // Desenha a rota sempre que muda.
  useEffect(() => {
    if (state !== "ready" || !map.current) return
    let cancelled = false
    const google = (window as unknown as { google: G }).google
    getRouteGeometry(shell, routeId)
      .then(({ geometry }) => {
        if (cancelled) return
        for (const l of layers.current) l.setMap(null)
        layers.current = []
        markers.current.clear()
        const bounds = new google.maps.LatLngBounds()
        if (geometry) {
          const path = decodePolyline(geometry)
          path.forEach((p) => bounds.extend(p))
          layers.current.push(new google.maps.Polyline({ map: map.current, path, strokeColor: "#2563eb", strokeOpacity: 0.8, strokeWeight: 4 }))
        }
        const wlat = n(warehouse?.lat)
        const wlng = n(warehouse?.lng)
        if (wlat !== null && wlng !== null) {
          bounds.extend({ lat: wlat, lng: wlng })
          layers.current.push(new google.maps.Marker({ map: map.current, position: { lat: wlat, lng: wlng }, title: warehouse?.name ?? "Armazém", label: { text: "A", color: "#fff", fontWeight: "700" } }))
        }
        for (const s of stops) {
          const o = orders.get(s.order_id)
          const lat = n(o?.lat)
          const lng = n(o?.lng)
          if (lat === null || lng === null) continue
          bounds.extend({ lat, lng })
          const m = new google.maps.Marker({
            map: map.current,
            position: { lat, lng },
            title: `${s.seq}. ${o?.source_ref ?? ""} · ${o?.locality ?? ""}`,
            label: { text: String(s.seq), color: "#fff", fontSize: "11px", fontWeight: "700" },
            icon: { path: google.maps.SymbolPath.CIRCLE, scale: 11, fillColor: "#0f172a", fillOpacity: 1, strokeColor: "#fff", strokeWeight: 2 },
          })
          m.addListener("click", () => onSelect(s.order_id))
          markers.current.set(s.order_id, m)
          layers.current.push(m)
        }
        if (!bounds.isEmpty()) map.current.fitBounds(bounds, 40)
      })
      .catch((err) => setState(errorMessage(err)))
    return () => {
      cancelled = true
    }
    // onSelect muda a cada render; a rota é o que interessa.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, routeId, stops, orders, warehouse, shell])

  // Paragem selecionada: realça e centra.
  useEffect(() => {
    if (state !== "ready") return
    const google = (window as unknown as { google: G }).google
    for (const [id, m] of markers.current) {
      const on = id === selected
      m.setIcon({ path: google.maps.SymbolPath.CIRCLE, scale: on ? 14 : 11, fillColor: on ? "#2563eb" : "#0f172a", fillOpacity: 1, strokeColor: "#fff", strokeWeight: 2 })
      m.setZIndex(on ? 1000 : undefined)
      if (on) map.current?.panTo(m.getPosition())
    }
  }, [selected, state])

  return (
    <div className="relative h-[460px] overflow-hidden rounded-lg border bg-muted">
      <div ref={box} className="absolute inset-0" />
      {state !== "ready" && (
        <div className="absolute inset-0 flex items-center justify-center p-4 text-center text-[12.5px] text-muted-foreground">
          {state === "loading" ? "A carregar o mapa…" : state === "off" ? "Mapa indisponível: o Google Maps não está configurado no tenant (Definições → Integrações)." : `Mapa indisponível: ${state}`}
        </div>
      )}
      {problem && (
        <div className="absolute inset-x-3 top-3 z-10 rounded-lg border border-amber-300 bg-amber-50/95 px-3 py-2 text-[12.5px] text-amber-900 shadow-sm">
          <b>Google Maps com problema na chave.</b> {problem}
        </div>
      )}
    </div>
  )
}
